import { flatten, intersection, isEmpty, keys, map, pickBy } from 'lodash';

import { AccountTypes, IAccount } from './accounts.service';

/**
 * Which users a singleUser / multiUser check question offers (questions.userFilter).
 * Same shape as a deployment's worker target.
 */
export interface IUserFilter {
  teams?: number[];
  locations?: number[];
  certifications?: number[];
  roles?: number[];
  permissions?: string[];
}

// Accounts that are not people a worker would name; the same types a deployment's worker target drops.
const EXCLUDED_TYPES: string[] = [
  AccountTypes.Observation,
  AccountTypes.Viewer,
  AccountTypes.Reporting,
  AccountTypes.Coordinator,
  AccountTypes.Wristband
];

const anyOf = (wanted: Array<number | string> = [], have: Array<number | string> = []): boolean =>
  isEmpty(wanted) || intersection(wanted, have).length > 0;

/**
 * Keys combine with AND, values within a key with OR, and an empty key does not constrain.
 * A user with no locations has access to all of them, so matches any location filter.
 * Disabled and inactive users never match.
 *
 * apps/mobile resolves the same filter offline from its cached user list; keep the two identical.
 */
export function matchesUserFilter(account: IAccount, filter: IUserFilter = {}): boolean {
  if (!account || account.disabledAt || account.active === 0 || EXCLUDED_TYPES.includes(account.type)) {
    return false;
  }

  return anyOf(filter.teams, account.groups)
    && (isEmpty(account.locations) || anyOf(filter.locations, account.locations))
    && anyOf(filter.certifications, account.certifications)
    && anyOf(filter.roles, account.roles)
    && anyOf(filter.permissions, keys(pickBy(account.permissions)));
}

/**
 * What to save for one filter key. The editor can only deselect IDs they were offered: a stored ID
 * with no option (its team/role/certification was disabled, or it is outside the editor's location
 * access) is kept. Otherwise re-saving the question would silently drop it and widen the filter.
 */
export function keepUnofferedIds(selected: any, stored: Array<number | string> = [], offered: string[] = []): any[] {
  return [...flatten([selected]), ...stored.filter((id) => !offered.includes(String(id)))];
}

/**
 * The pids in a stored user-question answer: a pid, an array of pids, or either as a string
 * (a stored pid can read back as a numeric string; an array can arrive JSON-encoded).
 * An N/A answer ([]) and "no answer" (0) both give [].
 */
export function answerPids(response: unknown): number[] {
  let value: unknown = response;

  if (typeof value === 'string' && value.trim().startsWith('[')) {
    try {
      value = JSON.parse(value);
    } catch (e) {
      value = [];
    }
  }

  return flatten([value]).map(Number).filter((id: number) => Number.isInteger(id) && id > 0);
}

/**
 * A user-question answer as the selected users' names, joined with ', '. A user disabled since
 * answering keeps their name, marked with disabledLabel; a pid with no account shows as unknownLabel.
 * Both labels are translated by the caller. Empty for N/A.
 */
export function formatUserAnswer(response: unknown, getAccount: (userID: number) => IAccount, disabledLabel: string,
  unknownLabel: string): string {
  return answerPids(response).map((userID) => {
    const account = getAccount(userID);
    if (!account) {
      return unknownLabel;
    }
    const name = `${account.firstname} ${account.lastname}`;
    return account.disabledAt || account.active === 0 ? `${name} (${disabledLabel})` : name;
  }).join(', ');
}

/**
 * Builds a userFilter from raw form values (a select gives a string, an array of strings, or nothing).
 * Empty keys are left out, so an unconstrained filter is {}.
 */
export function buildUserFilter(values: { [key in keyof IUserFilter]?: any }): IUserFilter {
  const list = (value: any): string[] => flatten([value]).filter((item) => item !== undefined && item !== null && item !== '');
  const filter: IUserFilter = {
    teams: map(list(values.teams), Number),
    locations: map(list(values.locations), Number),
    certifications: map(list(values.certifications), Number),
    roles: map(list(values.roles), Number),
    permissions: map(list(values.permissions), String)
  };

  return pickBy(filter, (value) => !isEmpty(value));
}
